import React from "react";
import { getCountryPayoutAvailability } from "../../utils/methodAvailability";

export default function PayoutMethodsPanel({
  earnings,
  stripeLinkedMethod,
  paypalLinkedMethod,
  testPayPalLinkedMethod,
  payPalLinkConfig,
  testPayPalLinkConfig,
  payPalButtonStates,
  savingMethod,
  handleStartStripeLink,
  handleOpenStripeDashboard,
  onRetryPayPal,
  onRetryTestPayPal,
  isAr,
}) {
  return (
    <div
      className="card border-0 shadow-sm mb-4 provider-center__panel-card pc-panel--payout"
      id="payout-methods-panel"
    >
      <div className="card-body">
        <div className="pc-section-head mb-4">
          <div className="pc-section-head__left">
            <span className="pc-icon-box pc-icon-box--blue">
              <i className="bi bi-credit-card-2-back" />
            </span>
            <h5 className="mb-0">{isAr ? "طرق السحب" : "Payout Methods"}</h5>
          </div>
          <span className="badge text-bg-light">{earnings.methods.length}</span>
        </div>

        <div className="alert alert-info border-0 mb-3">
          {isAr
            ? "اربط حساب Stripe أو PayPal الشخصي الحقيقي الخاص بالمستفيد نفسه. بعد اكتمال الربط فقط سيظهر الحساب كطريقة سحب صالحة."
            : "Link the beneficiary's own real personal Stripe or PayPal account. Only after linking is complete will the account appear as a valid payout method."}
        </div>

        {/* Stripe */}
        <div className="card border-0 bg-light mb-3 provider-center__method-card pc-payout-method">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="pc-method-icon pc-method-icon--stripe">
                    <i className="bi bi-credit-card" />
                  </span>
                  <h6 className="mb-0">
                    {isAr ? "حساب Stripe الشخصي" : "Personal Stripe Account"}
                  </h6>
                </div>
                <div className="small text-muted">
                  {stripeLinkedMethod
                    ? stripeLinkedMethod.destinationEmail ||
                      stripeLinkedMethod.accountName ||
                      stripeLinkedMethod.destinationReference
                    : isAr
                      ? "لا يوجد حساب Stripe مربوط بعد"
                      : "No Stripe account is linked yet"}
                </div>
                <div className="small text-muted mt-2">
                  {stripeLinkedMethod?.meta?.onboarding_complete
                    ? isAr
                      ? "✓ تم ربط الحساب ويمكن استخدامه للسحب إذا كان مدعومًا"
                      : "✓ Account is linked and ready for withdrawals"
                    : stripeLinkedMethod
                      ? isAr
                        ? "⚠ الحساب موجود لكن onboarding لم يكتمل"
                        : "⚠ Account exists but onboarding incomplete"
                      : isAr
                        ? "ابدأ الربط عبر Stripe Connect"
                        : "Start linking through Stripe Connect"}
                </div>
                {!getCountryPayoutAvailability(earnings, "payouts.legacy_methods.stripe")?.supported ? (
                  <div className="small text-warning mt-2">
                    <i className="bi bi-exclamation-circle me-1" />
                    {getCountryPayoutAvailability(earnings, "payouts.legacy_methods.stripe")?.reason}
                  </div>
                ) : null}
                {stripeLinkedMethod?.meta?.currently_due?.length ? (
                  <div className="small text-danger mt-2">
                    <i className="bi bi-info-circle me-1" />
                    {isAr ? "المطلوب من Stripe:" : "Due from Stripe:"}{" "}
                    {stripeLinkedMethod.meta.currently_due.join(", ")}
                  </div>
                ) : null}
              </div>

              <div className="d-flex flex-column gap-2 provider-center__method-actions" style={{ minWidth: "140px" }}>
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={handleStartStripeLink}
                  disabled={
                    savingMethod ||
                    !getCountryPayoutAvailability(earnings, "payouts.legacy_methods.stripe")?.supported
                  }
                >
                  {savingMethod
                    ? isAr
                      ? "جارٍ..."
                      : "Processing..."
                    : stripeLinkedMethod
                      ? isAr
                        ? "استكمال"
                        : "Continue"
                      : isAr
                        ? "ربط"
                        : "Link"}
                </button>
                {stripeLinkedMethod?.meta?.onboarding_complete ? (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    type="button"
                    onClick={handleOpenStripeDashboard}
                    disabled={savingMethod}
                  >
                    {isAr ? "اللوحة" : "Dashboard"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* PayPal */}
        <div className="card border-0 bg-light mb-3 provider-center__method-card pc-payout-method">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="pc-method-icon pc-method-icon--paypal">
                    <i className="bi bi-paypal" />
                  </span>
                  <h6 className="mb-0">
                    {isAr ? "حساب PayPal الشخصي" : "Personal PayPal Account"}
                  </h6>
                </div>
                <div className="small text-muted">
                  {paypalLinkedMethod
                    ? paypalLinkedMethod.destinationEmail ||
                      paypalLinkedMethod.accountName ||
                      paypalLinkedMethod.destinationReference
                    : isAr
                      ? "لا يوجد حساب PayPal مربوط بعد"
                      : "No PayPal account is linked yet"}
                </div>
                <div className="small text-muted mt-2">
                  {paypalLinkedMethod?.meta?.linked_account
                    ? isAr
                      ? "✓ تم ربط الحساب ويمكن استخدامه للسحب"
                      : "✓ Account is linked and ready"
                    : isAr
                      ? "اربط حساب PayPal الحقيقي"
                      : "Link your real PayPal account"}
                </div>
                {payPalLinkConfig.reason ? (
                  <div className="small text-warning mt-2">
                    <i className="bi bi-exclamation-circle me-1" />
                    {payPalLinkConfig.reason}
                  </div>
                ) : !getCountryPayoutAvailability(earnings, "payouts.legacy_methods.paypal")?.supported ? (
                  <div className="small text-warning mt-2">
                    <i className="bi bi-exclamation-circle me-1" />
                    {getCountryPayoutAvailability(earnings, "payouts.legacy_methods.paypal")?.reason}
                  </div>
                ) : null}
              </div>

              <div className="d-flex flex-column gap-2 provider-center__method-actions" style={{ minWidth: "140px" }}>
                {payPalLinkConfig.enabled ? (
                  <>
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      onClick={onRetryPayPal}
                      disabled={payPalButtonStates.paypal === "loading"}
                    >
                      {payPalButtonStates.paypal === "loading"
                        ? isAr
                          ? "جاري..."
                          : "Loading..."
                        : paypalLinkedMethod
                          ? isAr
                            ? "إعادة ربط"
                            : "Reconnect"
                          : isAr
                            ? "ربط"
                            : "Link"}
                    </button>
                    <div id="paypal-payout-link-button-paypal" />
                    {payPalButtonStates.paypal === "error" ? (
                      <div className="small text-warning mt-2 text-center">
                        {isAr ? "اضغط الزر مرة أخرى" : "Try again"}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <button className="btn btn-outline-secondary btn-sm" type="button" disabled>
                    {isAr ? "غير مفعّل" : "Disabled"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* PayPal Test (Sandbox) */}
        {getCountryPayoutAvailability(earnings, "payouts.legacy_methods.test_paypal")?.supported ||
        testPayPalLinkedMethod ? (
          <div className="card border-0 bg-light provider-center__method-card pc-payout-method pc-payout-method--test">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="pc-method-icon pc-method-icon--test">
                      <i className="bi bi-flask-fill" />
                    </span>
                    <h6 className="mb-0">
                      {isAr ? "حساب PayPal التجريبي" : "PayPal Sandbox (Admin)"}
                    </h6>
                  </div>
                  <div className="small text-muted">
                    {testPayPalLinkedMethod
                      ? testPayPalLinkedMethod.destinationEmail ||
                        testPayPalLinkedMethod.accountName ||
                        testPayPalLinkedMethod.destinationReference
                      : isAr
                        ? "لا يوجد حساب تجريبي مربوط بعد"
                        : "No sandbox account linked yet"}
                  </div>
                  <div className="small text-muted mt-2">
                    {testPayPalLinkedMethod?.meta?.linked_account
                      ? isAr
                        ? "✓ حساب تجريبي مربوط (للأدمن فقط)"
                        : "✓ Test account linked (admin only)"
                      : isAr
                        ? "للاختبار والتطوير فقط (يتطلب صلاحيات أدمن)"
                        : "For testing & development (admin required)"}
                  </div>
                  {testPayPalLinkConfig.reason ? (
                    <div className="small text-warning mt-2">
                      <i className="bi bi-exclamation-circle me-1" />
                      {testPayPalLinkConfig.reason}
                    </div>
                  ) : !getCountryPayoutAvailability(earnings, "payouts.legacy_methods.test_paypal")
                      ?.supported ? (
                    <div className="small text-warning mt-2">
                      <i className="bi bi-exclamation-circle me-1" />
                      {
                        getCountryPayoutAvailability(earnings, "payouts.legacy_methods.test_paypal")
                          ?.reason
                      }
                    </div>
                  ) : null}
                </div>

                <div className="d-flex flex-column gap-2 provider-center__method-actions" style={{ minWidth: "140px" }}>
                  {testPayPalLinkConfig.enabled ? (
                    <>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        onClick={onRetryTestPayPal}
                        disabled={payPalButtonStates.test_paypal === "loading"}
                      >
                        {payPalButtonStates.test_paypal === "loading"
                          ? isAr
                            ? "جاري..."
                            : "Loading..."
                          : testPayPalLinkedMethod
                            ? isAr
                              ? "إعادة ربط"
                              : "Reconnect"
                            : isAr
                              ? "ربط"
                              : "Link"}
                      </button>
                      <div id="paypal-payout-link-button-test_paypal" />
                      {payPalButtonStates.test_paypal === "error" ? (
                        <div className="small text-warning mt-2 text-center">
                          {isAr ? "اضغط الزر مرة أخرى" : "Try again"}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <button className="btn btn-outline-secondary btn-sm" type="button" disabled>
                      {isAr ? "غير مفعّل" : "Disabled"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
